import {ErrorHandler, Injectable, Injector, NgZone} from '@angular/core';
import {ToastService} from '../shared/toast/toast.service';

/**
 * Global error handler.
 *
 * Without this, an uncaught error thrown while rendering a component (for example the
 * mat-expansion-panel (opened)/(closed) crash on /publish/<namespace> cold loads) is only
 * logged to the console: the user sees nothing and the page can be left broken/blank, which
 * looks like a silent redirect/bounce with zero feedback.
 *
 * This handler still logs the original error (so it stays debuggable), but also surfaces a
 * user-facing snackbar so a crash is never completely silent.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

    private lastNotificationAt = 0;
    private readonly notificationCooldownMs = 10000;

    constructor(private injector: Injector) {
    }

    handleError(error: any): void {
        // Always log so the error remains visible in the console / any error-reporting tooling.
        console.error('Unhandled application error:', error);

        const now = Date.now();
        if (now - this.lastNotificationAt < this.notificationCooldownMs) {
            return;
        }
        this.lastNotificationAt = now;

        try {
            const zone = this.injector.get(NgZone);
            const toastService = this.injector.get(ToastService);
            zone.run(() => {
                toastService.error('אירעה שגיאה בלתי צפויה', {
                    message: 'נסו לרענן את הדף',
                    duration: 6000
                });
            });
        } catch (innerError) {
            // Never let the error handler itself throw.
            console.error('GlobalErrorHandler failed to surface notification:', innerError);
        }
    }
}
